import React, { useState, useLayoutEffect, useRef, useEffect } from "react";

export interface ITabMenuItem {
  title: string;
  onClick: () => void;
}

export interface ITabMenu {
  menuItems: ITabMenuItem[];
  selectedIndex?: number;
}

const TabMenu: React.FC<ITabMenu> = ({ menuItems, selectedIndex = 0 }) => {
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const underlineRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLLIElement | null)[]>([]);
  const containerRef = useRef<HTMLUListElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isMeasured, setIsMeasured] = useState(false);

  useEffect(() => {
    setActiveIndex(selectedIndex);
  }, [selectedIndex]);

  const handleClick = (index: number) => {
    setActiveIndex(index);
    menuItems[index].onClick();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateUnderline = () => {
    const activeTab = tabRefs.current[activeIndex];
    const underline = underlineRef.current;

    if (activeTab && underline && containerRef.current) {
      const tabRect = activeTab.getBoundingClientRect();
      const parentRect = containerRef.current.getBoundingClientRect();

      underline.style.width = `${tabRect.width}px`;
      underline.style.transform = `translateX(${tabRect.left - parentRect.left}px)`;
    }
  };

  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      const activeTab = tabRefs.current[activeIndex];
      const underline = underlineRef.current;
      const container = containerRef.current;

      if (activeTab && underline && container) {
        const tabRect = activeTab.getBoundingClientRect();
        const parentRect = container.getBoundingClientRect();

        underline.style.width = `${tabRect.width}px`;
        underline.style.transform = `translateX(${tabRect.left - parentRect.left}px)`;
        setIsMeasured(true);
      }

      // On narrow screens the tab strip scrolls; keep the active tab fully visible
      const scroller = scrollRef.current;
      if (activeTab && scroller) {
        const tabRect = activeTab.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();
        if (tabRect.left < scrollerRect.left) {
          scroller.scrollBy({ left: tabRect.left - scrollerRect.left, behavior: "smooth" });
        } else if (tabRect.right > scrollerRect.right) {
          scroller.scrollBy({ left: tabRect.right - scrollerRect.right, behavior: "smooth" });
        }
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [activeIndex, menuItems.length]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateUnderline();
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [updateUnderline]);

  return (
    <div className="relative w-full overflow-x-auto scrollbar-hide" ref={scrollRef}>
      <ul className="flex w-full min-w-max relative justify-between border-b-[1.5px] border-neutral-300" ref={containerRef}>
        {menuItems.map((item, index) => {
          // const isFirst = index === 0;
          // const isLast = index === menuItems.length - 1;
          // const alignmentClass = isFirst ? "text-start" : isLast ? "text-end" : "text-center";

          return (
            <li
              key={index}
              ref={el => (tabRefs.current[index] = el)}
              onClick={() => handleClick(index)}
              className={`cursor-pointer pt-2 pb-3 px-3 md:px-4 whitespace-nowrap font-medium text-base/[1.5rem] 
              ${activeIndex === index ? "text-[#0B1215]" : "text-[#696969]"}
              `}
            >
              {item.title}
            </li>
          );
        })}
        <div
          ref={underlineRef}
          className={`absolute bottom-[-1.5px] h-[1.5px] bg-[#0B1215] transition-all duration-300 ease-in-out`}
          style={{
            left: 0,
            width: "85px",
            opacity: isMeasured ? 1 : 0,
            transition:
              "opacity 0.2s ease, transform 0.3s ease, width 0.3s ease",
          }}
        />{" "}
      </ul>
    </div>
  );
};

export default TabMenu;
